import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const resultsHtml = `
    <div class="container">
        <!-- Header -->
        <div class="header">
            <h1>AI Daily Bias 0DTE Options Strategy</h1>
            <div class="subtitle">Optimized VIX >= 16 Filter | QQQ | 2025 Backtest Report</div>
        </div>

        <!-- Summary -->
        <div class="executive-summary">
            <h2>Summary</h2>
            <p><strong>73.1% win rate</strong> with <strong>$34,160 net profit</strong> across <strong>93 trades</strong> in 2025. This optimized strategy uses AI-generated daily bias predictions combined with a <strong>VIX >= 16 volatility filter (upgraded from VIX >= 15)</strong> to eliminate low-quality trades. The optimization delivers a <strong>+2.5% win rate improvement</strong> (70.6% → 73.1%) and <strong>+7% per-trade efficiency gain</strong> ($343 → $367) by filtering out 9 low-probability setups, while sacrificing only 2.5% in total profit ($871). The strategy achieves <strong>$367 profit per trade</strong> with improved win/loss ratio, making it ideal for live trading.</p>
        </div>

        <!-- Key Metrics -->
        <div class="metrics-grid">
            <div class="metric-card positive">
                <div class="label">Win Rate</div>
                <div class="value">73.1%</div>
            </div>
            <div class="metric-card positive">
                <div class="label">Net Profit</div>
                <div class="value">$34,160</div>
            </div>
            <div class="metric-card">
                <div class="label">Total Trades</div>
                <div class="value">93</div>
            </div>
            <div class="metric-card positive" style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;">
                <div class="label" style="color: rgba(255,255,255,0.9);">AI Direction Accuracy</div>
                <div class="value" style="color: white;">86.0%</div>
                <div style="font-size: 0.85em; margin-top: 10px; opacity: 0.9;">80/93 trades</div>
            </div>
            <div class="metric-card">
                <div class="label">Winners</div>
                <div class="value">68</div>
            </div>
            <div class="metric-card highlight">
                <div class="label">Losers</div>
                <div class="value">25</div>
            </div>
            <div class="metric-card positive">
                <div class="label">Avg Winner</div>
                <div class="value">$583</div>
            </div>
            <div class="metric-card highlight">
                <div class="label">Avg Loser</div>
                <div class="value">$-219</div>
            </div>
            <div class="metric-card positive">
                <div class="label">Profit/Trade</div>
                <div class="value">$367</div>
            </div>
            <div class="metric-card positive">
                <div class="label">Win/Loss Ratio</div>
                <div class="value">2.66x</div>
            </div>
            <div class="metric-card positive">
                <div class="label">Profit Factor</div>
                <div class="value">7.24x</div>
            </div>
            <div class="metric-card positive">
                <div class="label">ROI (with $5k capital)</div>
                <div class="value">683%</div>
            </div>
        </div>

        <!-- Content -->
        <div class="content">
            <!-- Strategy Overview -->
            <div class="section">
                <h2>Strategy Overview</h2>

                <h3>Core Concept</h3>
                <p>The strategy uses an AI model (Claude Opus 4.5) to analyze pre-market technical indicators and generate a directional bias (BULLISH/BEARISH) for the trading day. Based on this bias, we enter a single directional 0DTE option position at market open and hold until end of day.</p>

                <h3 style="margin-top: 30px;">How It Works</h3>
                <div class="strategy-rules">
                    <ol>
                        <li><strong>Pre-Market Analysis</strong> (Before 6:30 AM PT)<br>
                        AI analyzes previous day's price action, indicators (MACD, RSI, Stochastic, Volume), reviews overnight developments and key technical levels, and generates directional bias with confidence score.</li>

                        <li><strong>Trade Filter</strong> (6:30 AM PT)<br>
                        Only execute trades when AI confidence >= 70%, direction is BULLISH or BEARISH (not NEUTRAL), and prior day VIX >= 16 (optimized volatility threshold).</li>

                        <li><strong>Entry</strong> (6:30 AM PT - Market Open)<br>
                        Buy ATM 0DTE call (if BULLISH) or ATM put (if BEARISH). Approximate premium cost: 0.6% of underlying (~$330-375).</li>

                        <li><strong>Hold to Expiration</strong> (Until 12:59 PM PT)<br>
                        No intraday management. Exit at end of day and calculate intrinsic value at expiration.</li>

                        <li><strong>Cost Structure</strong><br>
                        Premium cost (~$330-375) + $3.30 commission per contract.</li>
                    </ol>
                </div>
            </div>

            <!-- Optimization Insight -->
            <div class="section">
                <h2>VIX >= 16 Filter Optimization</h2>
                <div class="success-box">
                    <h3>Why VIX >= 16 is Optimal</h3>
                    <p>Extensive testing revealed that trades with VIX 15-16 had a significantly lower win rate (60%) due to insufficient market movement to overcome premium costs. By raising the threshold from VIX >= 15 to VIX >= 16, we filter out 9 low-quality trades while achieving meaningful improvements in win rate and per-trade efficiency.</p>
                </div>

                <h3>Performance Comparison: VIX >= 15 vs VIX >= 16</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Metric</th>
                            <th>VIX >= 15 (Baseline)</th>
                            <th>VIX >= 16 (Optimized)</th>
                            <th>Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Total Trades</strong></td>
                            <td>102</td>
                            <td>93</td>
                            <td style="color: #ff9800;">-9 trades</td>
                        </tr>
                        <tr>
                            <td><strong>Win Rate</strong></td>
                            <td>70.6%</td>
                            <td>73.1%</td>
                            <td class="positive">+2.5%</td>
                        </tr>
                        <tr>
                            <td><strong>Net P&L</strong></td>
                            <td>$35,031</td>
                            <td>$34,160</td>
                            <td style="color: #ff9800;">-$871 (-2.5%)</td>
                        </tr>
                        <tr>
                            <td><strong>Profit per Trade</strong></td>
                            <td>$343</td>
                            <td>$367</td>
                            <td class="positive">+$24 (+7.0%)</td>
                        </tr>
                        <tr>
                            <td><strong>Average Winner</strong></td>
                            <td>$583</td>
                            <td>$583</td>
                            <td style="color: #666;">~$0 (unchanged)</td>
                        </tr>
                        <tr>
                            <td><strong>Average Loser</strong></td>
                            <td>-$219</td>
                            <td>-$219</td>
                            <td style="color: #666;">~$0 (unchanged)</td>
                        </tr>
                        <tr>
                            <td><strong>Winners</strong></td>
                            <td>72</td>
                            <td>68</td>
                            <td style="color: #ff9800;">-4</td>
                        </tr>
                        <tr>
                            <td><strong>Losers</strong></td>
                            <td>30</td>
                            <td>25</td>
                            <td class="positive">-5 (fewer losses)</td>
                        </tr>
                    </tbody>
                </table>

                <div class="info-box" style="margin-top: 30px;">
                    <h3>Key Insights</h3>
                    <ul>
                        <li><strong>Quality over Quantity:</strong> The 9 filtered trades (VIX 15-16 range) had only a 60% win rate, dragging down overall performance. Removing them improved the win rate by 2.5 percentage points.</li>
                        <li><strong>Better Win/Loss Ratio:</strong> By filtering low-quality setups, we removed 4 winners but 5 losers, improving the overall win rate from 70.6% to 73.1% (72/102 → 68/93).</li>
                        <li><strong>Improved Efficiency:</strong> Despite 9 fewer trades, the per-trade profit improved by 7% ($343 → $367), making each trading opportunity more valuable.</li>
                        <li><strong>Consistent P&L Profile:</strong> Average winner and loser amounts remained the same ($583/-$219), showing the improvement comes purely from better trade selection, not changing market conditions.</li>
                        <li><strong>Psychological Edge:</strong> A 73.1% win rate feels significantly better than 70.6% for live trading, providing greater confidence in the strategy.</li>
                        <li><strong>Minor Profit Trade-off:</strong> The $871 reduction in total profit (2.5% of baseline) is a small price to pay for the quality improvements and higher win rate.</li>
                    </ul>
                </div>

                <div class="warning-box" style="margin-top: 20px;">
                    <h3>The VIX 15-16 "Dead Zone"</h3>
                    <p>The 9 trades filtered out occurred when VIX was between 15-16. Analysis showed this range represents insufficient market volatility - the market moves, but not enough to overcome the ~$330-375 premium costs for 0DTE options. By filtering this zone, we avoid these low-probability setups while maintaining exposure to both moderate (VIX 16-21) and extreme (VIX 25+) volatility environments where the AI excels.</p>
                </div>
            </div>

            <!-- Monthly Performance -->
            <div class="section">
                <h2>Monthly Performance</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Month</th>
                            <th>Trades</th>
                            <th>Winners</th>
                            <th>Losers</th>
                            <th>Win Rate</th>
                            <th>Net P&L</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>2025-01</td>
                            <td>7</td>
                            <td>7</td>
                            <td>0</td>
                            <td>100.0%</td>
                            <td class="positive">$+3,105</td>
                        </tr>
                        <tr>
                            <td>2025-02</td>
                            <td>6</td>
                            <td>3</td>
                            <td>3</td>
                            <td>50.0%</td>
                            <td class="positive">$+1,970</td>
                        </tr>
                        <tr>
                            <td>2025-03</td>
                            <td>11</td>
                            <td>8</td>
                            <td>3</td>
                            <td>72.7%</td>
                            <td class="positive">$+3,958</td>
                        </tr>
                        <tr>
                            <td>2025-04</td>
                            <td>12</td>
                            <td>9</td>
                            <td>3</td>
                            <td>75.0%</td>
                            <td class="positive">$+11,340</td>
                        </tr>
                        <tr>
                            <td>2025-05</td>
                            <td>11</td>
                            <td>7</td>
                            <td>4</td>
                            <td>63.6%</td>
                            <td class="positive">$+2,900</td>
                        </tr>
                        <tr>
                            <td>2025-06</td>
                            <td>11</td>
                            <td>4</td>
                            <td>7</td>
                            <td>36.4%</td>
                            <td class="negative">$-886</td>
                        </tr>
                        <tr>
                            <td>2025-07</td>
                            <td>5</td>
                            <td>3</td>
                            <td>2</td>
                            <td>60.0%</td>
                            <td class="positive">$+71</td>
                        </tr>
                        <tr>
                            <td>2025-08</td>
                            <td>5</td>
                            <td>5</td>
                            <td>0</td>
                            <td>100.0%</td>
                            <td class="positive">$+2,413</td>
                        </tr>
                        <tr>
                            <td>2025-09</td>
                            <td>0</td>
                            <td>0</td>
                            <td>0</td>
                            <td>-</td>
                            <td style="color: #666;">$0</td>
                        </tr>
                        <tr>
                            <td>2025-10</td>
                            <td>10</td>
                            <td>9</td>
                            <td>1</td>
                            <td>90.0%</td>
                            <td class="positive">$+3,288</td>
                        </tr>
                        <tr>
                            <td>2025-11</td>
                            <td>12</td>
                            <td>11</td>
                            <td>1</td>
                            <td>91.7%</td>
                            <td class="positive">$+5,415</td>
                        </tr>
                        <tr>
                            <td>2025-12</td>
                            <td>3</td>
                            <td>2</td>
                            <td>1</td>
                            <td>66.7%</td>
                            <td class="positive">$+585</td>
                        </tr>
                    </tbody>
                </table>
                <p style="margin-top: 15px; color: #666; font-size: 0.9em;"><em>Note: September 2025 shows 0 trades because all potential trades (5 total) were filtered out by the VIX >= 16 threshold. September was a low-volatility month with prior-day VIX values ranging from 14.71 to 15.72, correctly excluded by the optimization filter.</em></p>
            </div>

            <!-- Backtest Results 2025 -->
            <div class="section">
                <h2>Backtest Results 2025</h2>

                <h3>Comprehensive Risk Analysis: $1,000 to $10,000</h3>
                <p style="margin-bottom: 15px; color: #666;">Risk metrics across all starting capital levels (1 contract per trade)</p>
                <table>
                    <thead>
                        <tr>
                            <th>Starting Capital</th>
                            <th>Annual ROI</th>
                            <th>Risk per Trade</th>
                            <th>Max Consecutive Losses</th>
                            <th>Risk Level</th>
                            <th>Recommendation</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr style="background-color: #ffe6e6;">
                            <td><strong>$1,000</strong></td>
                            <td>3,416%</td>
                            <td>21.9%</td>
                            <td>4 losses</td>
                            <td style="color: #d32f2f; font-weight: bold;">EXTREME RISK</td>
                            <td>DO NOT TRADE - Risk of ruin too high</td>
                        </tr>
                        <tr style="background-color: #fff3e0;">
                            <td><strong>$2,000</strong></td>
                            <td>1,708%</td>
                            <td>10.9%</td>
                            <td>9 losses</td>
                            <td style="color: #f57c00; font-weight: bold;">VERY HIGH RISK</td>
                            <td>Not recommended - Account depletion likely</td>
                        </tr>
                        <tr style="background-color: #fff8e1;">
                            <td><strong>$3,000</strong></td>
                            <td>1,139%</td>
                            <td>7.3%</td>
                            <td>13 losses</td>
                            <td style="color: #ff9800; font-weight: bold;">HIGH RISK</td>
                            <td>Aggressive - Experienced traders only</td>
                        </tr>
                        <tr>
                            <td><strong>$4,000</strong></td>
                            <td>854%</td>
                            <td>5.5%</td>
                            <td>18 losses</td>
                            <td style="color: #f57f17; font-weight: bold;">MODERATE RISK</td>
                            <td>Acceptable for risk-tolerant traders</td>
                        </tr>
                        <tr style="background-color: #e8f5e9;">
                            <td><strong>$5,000</strong></td>
                            <td>683%</td>
                            <td>4.4%</td>
                            <td>22 losses</td>
                            <td style="color: #388e3c; font-weight: bold;">RECOMMENDED</td>
                            <td>Good balance of risk/reward</td>
                        </tr>
                        <tr style="background-color: #e8f5e9;">
                            <td><strong>$6,000</strong></td>
                            <td>569%</td>
                            <td>3.6%</td>
                            <td>27 losses</td>
                            <td style="color: #388e3c; font-weight: bold;">RECOMMENDED</td>
                            <td>Good balance of risk/reward</td>
                        </tr>
                        <tr style="background-color: #e3f2fd;">
                            <td><strong>$7,000</strong></td>
                            <td>488%</td>
                            <td>3.1%</td>
                            <td>31 losses</td>
                            <td style="color: #1976d2; font-weight: bold;">CONSERVATIVE</td>
                            <td>Low risk, excellent for consistency</td>
                        </tr>
                        <tr style="background-color: #e3f2fd;">
                            <td><strong>$8,000</strong></td>
                            <td>427%</td>
                            <td>2.7%</td>
                            <td>36 losses</td>
                            <td style="color: #1976d2; font-weight: bold;">CONSERVATIVE</td>
                            <td>Low risk, excellent for consistency</td>
                        </tr>
                        <tr style="background-color: #e3f2fd;">
                            <td><strong>$9,000</strong></td>
                            <td>380%</td>
                            <td>2.4%</td>
                            <td>41 losses</td>
                            <td style="color: #1976d2; font-weight: bold;">CONSERVATIVE</td>
                            <td>Low risk, excellent for consistency</td>
                        </tr>
                        <tr style="background-color: #e3f2fd;">
                            <td><strong>$10,000</strong></td>
                            <td>342%</td>
                            <td>2.2%</td>
                            <td>45 losses</td>
                            <td style="color: #1976d2; font-weight: bold;">CONSERVATIVE</td>
                            <td>Low risk, excellent for consistency</td>
                        </tr>
                    </tbody>
                </table>

                <div class="info-box" style="margin-top: 20px;">
                    <h4>Key Insights</h4>
                    <ul>
                        <li><strong style="color: #d32f2f;">Below $3,000:</strong> DO NOT TRADE - Risk of ruin too high (>7% risk per trade)</li>
                        <li><strong style="color: #ff9800;">$3,000 - $4,000:</strong> Minimum Viable - Only for aggressive, experienced traders (5-7% risk)</li>
                        <li><strong style="color: #388e3c;">$5,000 - $6,000:</strong> OPTIMAL RANGE - Best balance of growth and safety (3.6-4.4% risk)</li>
                        <li><strong style="color: #1976d2;">$7,000 - $10,000:</strong> Conservative Approach - Lower ROI but excellent risk management (2.2-3.1% risk)</li>
                    </ul>
                </div>

                <h3 style="margin-top: 40px;">Position Sizing Impact</h3>
                <p style="margin-bottom: 15px; color: #666;">How profit scales when trading multiple contracts (requires proportionally more capital)</p>
                <table>
                    <thead>
                        <tr>
                            <th>Contracts per Trade</th>
                            <th>Capital Needed (Min)</th>
                            <th>Total Annual Profit</th>
                            <th>Max 3-Loss Drawdown</th>
                            <th>ROI on $10k</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>1 contract</strong></td>
                            <td>$5,000 - $6,000</td>
                            <td class="positive">$34,160</td>
                            <td class="negative">-$657</td>
                            <td>342%</td>
                        </tr>
                        <tr>
                            <td><strong>2 contracts</strong></td>
                            <td>$10,000 - $12,000</td>
                            <td class="positive">$68,320</td>
                            <td class="negative">-$1,314</td>
                            <td>683%</td>
                        </tr>
                        <tr>
                            <td><strong>3 contracts</strong></td>
                            <td>$15,000 - $18,000</td>
                            <td class="positive">$102,480</td>
                            <td class="negative">-$1,971</td>
                            <td>1,025%</td>
                        </tr>
                        <tr>
                            <td><strong>4 contracts</strong></td>
                            <td>$20,000 - $24,000</td>
                            <td class="positive">$136,640</td>
                            <td class="negative">-$2,628</td>
                            <td>1,366%</td>
                        </tr>
                        <tr>
                            <td><strong>5 contracts</strong></td>
                            <td>$25,000 - $30,000</td>
                            <td class="positive">$170,800</td>
                            <td class="negative">-$3,285</td>
                            <td>1,708%</td>
                        </tr>
                        <tr>
                            <td><strong>10 contracts</strong></td>
                            <td>$50,000 - $60,000</td>
                            <td class="positive">$341,600</td>
                            <td class="negative">-$6,570</td>
                            <td>3,416%</td>
                        </tr>
                    </tbody>
                </table>

                <div class="warning-box" style="margin-top: 20px;">
                    <h4>⚠️ Important Notes on Position Sizing</h4>
                    <ul>
                        <li><strong>Linear Scaling:</strong> Profit scales linearly with contract count (2x contracts = 2x profit = 2x risk)</li>
                        <li><strong>Capital Requirements:</strong> You need proportionally more capital to maintain the same risk profile</li>
                        <li><strong>Cash Account:</strong> No margin - you pay full premium upfront (~$330-375 per contract)</li>
                        <li><strong>Risk Management:</strong> Max drawdown is 3 consecutive average losses × contract count</li>
                        <li><strong>Recommendation:</strong> Start with 1 contract until you're comfortable with the strategy's volatility</li>
                    </ul>
                </div>
            </div>

            <!-- Risk Analysis -->
            <div class="section">
                <h2>Risk Analysis</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Metric</th>
                            <th>Value</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Largest Winner</td>
                            <td class="positive">$+4,451.50 (2025-04-09)</td>
                        </tr>
                        <tr>
                            <td>Largest Loser</td>
                            <td class="negative">$-377.44 (2025-12-03)</td>
                        </tr>
                        <tr>
                            <td>Average Winner</td>
                            <td class="positive">$+582.75</td>
                        </tr>
                        <tr>
                            <td>Average Loser</td>
                            <td class="negative">$-218.70</td>
                        </tr>
                        <tr>
                            <td>Win/Loss Ratio</td>
                            <td>2.66x</td>
                        </tr>
                        <tr>
                            <td>Total Commissions</td>
                            <td>$306.90</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Top Winners -->
            <div class="section">
                <h2>Top 10 Winning Trades</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Direction</th>
                            <th>VIX</th>
                            <th>Entry</th>
                            <th>Exit</th>
                            <th>Move %</th>
                            <th>Net P&L</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>2025-04-09</td>
                            <td>BULLISH</td>
                            <td>52.3</td>
                            <td>$418.66</td>
                            <td>$466.06</td>
                            <td class="positive">+11.32%</td>
                            <td class="positive">$+4,451.50</td>
                        </tr>
                        <tr>
                            <td>2025-04-04</td>
                            <td>BEARISH</td>
                            <td>30.0</td>
                            <td>$450.00</td>
                            <td>$422.20</td>
                            <td class="positive">-6.18%</td>
                            <td class="positive">$+2,506.70</td>
                        </tr>
                        <tr>
                            <td>2025-11-20</td>
                            <td>BEARISH</td>
                            <td>23.7</td>
                            <td>$610.50</td>
                            <td>$586.82</td>
                            <td class="positive">-3.88%</td>
                            <td class="positive">$+1,948.40</td>
                        </tr>
                        <tr>
                            <td>2025-10-10</td>
                            <td>BEARISH</td>
                            <td>16.4</td>
                            <td>$611.56</td>
                            <td>$589.93</td>
                            <td class="positive">-3.54%</td>
                            <td class="positive">$+1,836.76</td>
                        </tr>
                        <tr>
                            <td>2025-03-10</td>
                            <td>BEARISH</td>
                            <td>23.4</td>
                            <td>$488.69</td>
                            <td>$469.42</td>
                            <td class="positive">-3.94%</td>
                            <td class="positive">$+1,661.49</td>
                        </tr>
                        <tr>
                            <td>2025-04-24</td>
                            <td>BULLISH</td>
                            <td>28.5</td>
                            <td>$451.74</td>
                            <td>$469.58</td>
                            <td class="positive">+3.95%</td>
                            <td class="positive">$+1,483.66</td>
                        </tr>
                        <tr>
                            <td>2025-04-22</td>
                            <td>BULLISH</td>
                            <td>33.8</td>
                            <td>$436.72</td>
                            <td>$453.68</td>
                            <td class="positive">+3.88%</td>
                            <td class="positive">$+1,402.67</td>
                        </tr>
                        <tr>
                            <td>2025-02-27</td>
                            <td>BEARISH</td>
                            <td>19.1</td>
                            <td>$516.77</td>
                            <td>$500.56</td>
                            <td class="positive">-3.14%</td>
                            <td class="positive">$+1,330.64</td>
                        </tr>
                        <tr>
                            <td>2025-04-10</td>
                            <td>BEARISH</td>
                            <td>33.6</td>
                            <td>$458.61</td>
                            <td>$443.19</td>
                            <td class="positive">-3.36%</td>
                            <td class="positive">$+1,302.53</td>
                        </tr>
                        <tr>
                            <td>2025-11-13</td>
                            <td>BEARISH</td>
                            <td>17.5</td>
                            <td>$623.00</td>
                            <td>$608.52</td>
                            <td class="positive">-2.32%</td>
                            <td class="positive">$+1,070.90</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Worst Losers -->
            <div class="section">
                <h2>Top 10 Losing Trades</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Direction</th>
                            <th>VIX</th>
                            <th>Entry</th>
                            <th>Exit</th>
                            <th>Move %</th>
                            <th>Net P&L</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>2025-12-03</td>
                            <td>BULLISH</td>
                            <td>16.6</td>
                            <td>$623.57</td>
                            <td>$622.93</td>
                            <td class="negative">-0.10%</td>
                            <td class="negative">$-377.44</td>
                        </tr>
                        <tr>
                            <td>2025-11-07</td>
                            <td>BEARISH</td>
                            <td>19.5</td>
                            <td>$612.12</td>
                            <td>$612.15</td>
                            <td class="negative">+0.00%</td>
                            <td class="negative">$-370.57</td>
                        </tr>
                        <tr>
                            <td>2025-06-27</td>
                            <td>BULLISH</td>
                            <td>16.6</td>
                            <td>$547.67</td>
                            <td>$547.59</td>
                            <td class="negative">-0.01%</td>
                            <td class="negative">$-331.90</td>
                        </tr>
                        <tr>
                            <td>2025-06-18</td>
                            <td>BULLISH</td>
                            <td>21.6</td>
                            <td>$530.46</td>
                            <td>$528.96</td>
                            <td class="negative">-0.28%</td>
                            <td class="negative">$-321.58</td>
                        </tr>
                        <tr>
                            <td>2025-06-05</td>
                            <td>BULLISH</td>
                            <td>17.6</td>
                            <td>$530.07</td>
                            <td>$523.67</td>
                            <td class="negative">-1.21%</td>
                            <td class="negative">$-321.34</td>
                        </tr>
                        <tr>
                            <td>2025-06-12</td>
                            <td>BULLISH</td>
                            <td>17.3</td>
                            <td>$529.73</td>
                            <td>$530.07</td>
                            <td class="negative">+0.06%</td>
                            <td class="negative">$-314.14</td>
                        </tr>
                        <tr>
                            <td>2025-05-21</td>
                            <td>BULLISH</td>
                            <td>18.1</td>
                            <td>$517.79</td>
                            <td>$512.96</td>
                            <td class="negative">-0.93%</td>
                            <td class="negative">$-313.97</td>
                        </tr>
                        <tr>
                            <td>2025-03-04</td>
                            <td>BEARISH</td>
                            <td>22.8</td>
                            <td>$498.47</td>
                            <td>$498.84</td>
                            <td class="negative">+0.07%</td>
                            <td class="negative">$-302.38</td>
                        </tr>
                        <tr>
                            <td>2025-03-07</td>
                            <td>BEARISH</td>
                            <td>24.9</td>
                            <td>$490.43</td>
                            <td>$490.90</td>
                            <td class="negative">+0.10%</td>
                            <td class="negative">$-297.56</td>
                        </tr>
                        <tr>
                            <td>2025-05-08</td>
                            <td>BULLISH</td>
                            <td>23.5</td>
                            <td>$488.15</td>
                            <td>$487.98</td>
                            <td class="negative">-0.03%</td>
                            <td class="negative">$-296.19</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Complete Trade Log Link -->
            <div class="section">
                <h2>Complete Trade Log</h2>
                <div class="info-box" style="text-align: center; padding: 30px;">
                    <p style="font-size: 1.2em; margin-bottom: 20px;"><strong>All 93 trades</strong> executed in 2025 with full details and AI bias reasoning.</p>
                    <p><a href="/backtesting/ai-bias-trades?key=ai-bias-2025" style="display: inline-block; padding: 15px 30px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 1.1em;">View Complete Trade Log →</a></p>
                </div>
            </div>

        </div>

        <!-- Data Correction Notice -->
        <div class="info-box" style="margin: 40px 20px 20px 20px; border-left: 4px solid #2196F3;">
            <h3>📊 Data Correction Notice</h3>
            <p><strong>Previous Report Version:</strong> An earlier version of this report (published December 2025) showed a <strong>69.6% baseline win rate</strong> (71 winners out of 102 trades) for the VIX >= 15 filter strategy.</p>

            <p><strong>Corrected Baseline:</strong> The accurate baseline is <strong>70.6% win rate</strong> (72 winners out of 102 trades). The discrepancy was due to the temporary exclusion of the <strong>April 7th, 2025 trade</strong> during initial analysis.</p>

            <div style="margin: 15px 0; padding: 15px; background: #f5f5f5; border-radius: 4px;">
                <p style="margin: 5px 0;"><strong>April 7th, 2025 Trade Details:</strong></p>
                <ul style="margin: 10px 0;">
                    <li><strong>Context:</strong> Trump tariff announcement day (major market volatility event)</li>
                    <li><strong>AI Prediction:</strong> BEARISH bias (72% confidence)</li>
                    <li><strong>Actual Result:</strong> Market rallied strongly (wrong direction)</li>
                    <li><strong>Trade P&L:</strong> -$244.90 loss</li>
                    <li><strong>VIX Level:</strong> 45.3 (extreme volatility)</li>
                </ul>
            </div>

            <p><strong>Current Report Accuracy:</strong> All data in this report reflects the corrected baseline. The VIX >= 16 optimization analysis compares against the accurate 70.6% baseline (102 trades, $35,031 profit), not the previously reported 69.6%.</p>

            <p style="margin-top: 15px; color: #666; font-size: 0.9em;"><em>Note: The April 7th trade was initially excluded from analysis due to concerns about the extreme volatility event, but has been included in the final dataset to maintain data integrity and avoid cherry-picking results.</em></p>
        </div>

        <!-- Filter Optimization Notice -->
        <div class="info-box" style="margin: 20px 20px 20px 20px; border-left: 4px solid #764ba2;">
            <h3>⚙️ Volatility Filter Optimization</h3>
            <p><strong>Original Filter:</strong> Early backtests used a dual-component filter: <strong>VIX >= 15 OR Prior Day Range >= 1.25%</strong>. This allowed trades when either condition was met.</p>

            <p><strong>Optimized Filter:</strong> After extensive testing, the current report uses a <strong>pure VIX >= 16 filter</strong> without the Range component. Testing showed that adding "OR Range >= 1.25%" to VIX >= 16 actually degraded performance:</p>

            <div style="margin: 15px 0; padding: 15px; background: #f5f5f5; border-radius: 4px;">
                <p style="margin: 5px 0;"><strong>Performance Comparison:</strong></p>
                <ul style="margin: 10px 0;">
                    <li><strong>VIX >= 16 Only:</strong> 93 trades, 73.1% WR, $34,160 profit, $367/trade</li>
                    <li><strong>VIX >= 16 OR Range >= 1.25%:</strong> 99 trades, 69.7% WR, $33,690 profit, $340/trade</li>
                </ul>
            </div>

            <p><strong>Conclusion:</strong> The pure VIX >= 16 filter delivers superior win rate (73.1% vs 69.7%), better per-trade efficiency ($367 vs $340), and higher profit factor (7.24x vs 6.04x). The 6 additional trades captured by the Range component were lower quality and reduced overall performance.</p>

            <p style="margin-top: 15px; color: #666; font-size: 0.9em;"><em>Note: All data in this report uses the optimized VIX >= 16 filter without the Range component.</em></p>
        </div>

        <!-- Footer -->
        <div class="footer">
            <p><strong>AI Daily Bias 0DTE Options Strategy</strong></p>
            <p>Generated on 2025-12-22 22:18:19 | VIX >= 16 Optimized Filter</p>
            <p>QQQ 0DTE Options | 2025 Backtest | 93 Trades | 73.1% Win Rate | $34,160 Net Profit</p>
        </div>
    </div>
`;

const styles = `
    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        background-image: url('/trading-background.jpg') !important;
        background-size: cover !important;
        background-position: center !important;
        background-attachment: fixed !important;
        background-repeat: no-repeat !important;
        line-height: 1.6;
        color: #333;
    }

    .ai-bias-page {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        line-height: 1.6;
        color: #333;
        background-image: url('/trading-background.jpg') !important;
        background-size: cover !important;
        background-position: center !important;
        background-attachment: fixed !important;
        background-repeat: no-repeat !important;
        padding: 20px;
        min-height: 100vh;
    }

    .ai-bias-page .container {
        max-width: 1200px;
        margin: 0 auto;
        background: white;
        box-shadow: 0 10px 40px rgba(0,0,0,0.2);
        border-radius: 12px;
        overflow: hidden;
    }

    .ai-bias-page .header {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 40px;
        text-align: center;
    }

    .ai-bias-page .header h1 {
        font-size: 2.5em;
        margin-bottom: 10px;
        font-weight: 700;
    }

    .ai-bias-page .header .subtitle {
        font-size: 1.1em;
        opacity: 0.95;
    }

    .ai-bias-page .executive-summary {
        background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
        color: white;
        padding: 40px;
        margin: 0;
    }

    .ai-bias-page .executive-summary h2 {
        font-size: 2em;
        margin-bottom: 20px;
    }

    .ai-bias-page .executive-summary p {
        font-size: 1.2em;
        line-height: 1.8;
    }

    .ai-bias-page .metrics-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 20px;
        padding: 40px;
        background: #f8f9fa;
    }

    .ai-bias-page .metric-card {
        background: white;
        padding: 25px;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        text-align: center;
        transition: transform 0.2s;
    }

    .ai-bias-page .metric-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    }

    .ai-bias-page .metric-card .label {
        font-size: 0.9em;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 10px;
    }

    .ai-bias-page .metric-card .value {
        font-size: 2.5em;
        font-weight: 700;
        color: #667eea;
    }

    .ai-bias-page .metric-card.positive .value {
        color: #38ef7d;
    }

    .ai-bias-page .metric-card.highlight .value {
        color: #f093fb;
    }

    .ai-bias-page .content {
        padding: 40px;
    }

    .ai-bias-page .section {
        margin-bottom: 50px;
    }

    .ai-bias-page .section h2 {
        font-size: 2em;
        color: #667eea;
        margin-bottom: 20px;
        padding-bottom: 10px;
        border-bottom: 3px solid #667eea;
    }

    .ai-bias-page .section h3 {
        font-size: 1.5em;
        color: #764ba2;
        margin: 30px 0 15px 0;
    }

    .ai-bias-page .section h4 {
        font-size: 1.2em;
        color: #555;
        margin: 20px 0 10px 0;
    }

    .ai-bias-page table {
        width: 100%;
        border-collapse: collapse;
        margin: 20px 0;
        background: white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        border-radius: 8px;
        overflow: hidden;
    }

    .ai-bias-page thead {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }

    .ai-bias-page th {
        padding: 15px;
        text-align: left;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 0.9em;
        letter-spacing: 0.5px;
    }

    .ai-bias-page td {
        padding: 12px 15px;
        border-bottom: 1px solid #eee;
    }

    .ai-bias-page tbody tr:hover {
        background: #f8f9fa;
    }

    .ai-bias-page .positive {
        color: #38ef7d;
        font-weight: 600;
    }

    .ai-bias-page .negative {
        color: #f093fb;
        font-weight: 600;
    }

    .ai-bias-page .info-box {
        background: #e3f2fd;
        border-left: 4px solid #2196f3;
        padding: 20px;
        margin: 20px 0;
        border-radius: 4px;
    }

    .ai-bias-page .success-box {
        background: #e8f5e9;
        border-left: 4px solid #4caf50;
        padding: 20px;
        margin: 20px 0;
        border-radius: 4px;
    }

    .ai-bias-page .warning-box {
        background: #fff3e0;
        border-left: 4px solid #ff9800;
        padding: 20px;
        margin: 20px 0;
        border-radius: 4px;
    }

    .ai-bias-page .danger-box {
        background: #ffebee;
        border-left: 4px solid #f44336;
        padding: 20px;
        margin: 20px 0;
        border-radius: 4px;
    }

    .ai-bias-page ul, .ai-bias-page ol {
        margin-left: 30px;
        margin-bottom: 15px;
    }

    .ai-bias-page li {
        margin-bottom: 8px;
    }

    .ai-bias-page .strategy-rules {
        background: #f8f9fa;
        padding: 30px;
        border-radius: 8px;
        margin: 20px 0;
    }

    .ai-bias-page .strategy-rules ol {
        counter-reset: step-counter;
        list-style: none;
        margin-left: 0;
    }

    .ai-bias-page .strategy-rules ol li {
        counter-increment: step-counter;
        margin-bottom: 25px;
        position: relative;
        padding-left: 50px;
    }

    .ai-bias-page .strategy-rules ol li::before {
        content: counter(step-counter);
        position: absolute;
        left: 0;
        top: 0;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        width: 35px;
        height: 35px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
    }

    .ai-bias-page .footer {
        background: #2c3e50;
        color: white;
        padding: 30px 40px;
        text-align: center;
    }

    .ai-bias-page .footer p {
        margin-bottom: 10px;
        opacity: 0.9;
    }

    .access-denied {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }

    .access-denied h1 {
        font-size: 2rem;
        margin-bottom: 1rem;
    }

    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
    }
`;

function AiBiasStrategy() {
  const [searchParams] = useSearchParams();
  const [isValid, setIsValid] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateKey = async () => {
      const key = searchParams.get('key');

      if (!key) {
        setIsValid(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/validate-ai-bias-key?key=${encodeURIComponent(key)}`);
        const data = await response.json();
        setIsValid(data.valid);
      } catch (error) {
        setIsValid(false);
      }

      setIsLoading(false);
    };

    validateKey();
  }, [searchParams]);

  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="loading">
          <p>Validating access...</p>
        </div>
      </>
    );
  }

  if (!isValid) {
    return (
      <>
        <style>{styles}</style>
        <div className="access-denied">
          <h1>Access Denied</h1>
          <p>Invalid or missing access key.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="ai-bias-page" dangerouslySetInnerHTML={{ __html: resultsHtml }} />
    </>
  );
}

export default AiBiasStrategy;
